import { useCallback, useEffect, useRef, useState } from 'react'

// Manages a MediaRecorder lifecycle. Returns the recording state plus the
// captured audio blob and a playback URL. The blob is what gets uploaded;
// the URL is for the in-page <audio> preview.

// Preference-ordered container/codec candidates. Chrome, Edge, Firefox and
// Android record webm/opus; iOS Safari and macOS Safari < 18.4 only record
// audio/mp4 (AAC) — Safari gained webm/opus in 18.4. If nothing matches we
// construct MediaRecorder with no mimeType and let the browser use its
// default (never throws), then read the real type back off the recorder.
const CANDIDATE_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
]

function pickMimeType() {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return undefined
  return CANDIDATE_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type))
}

// A "recording" smaller than this is garbage from a failed capture (dead or
// switched input device, mid-recording MediaRecorder error). Surface it as
// an error instead of showing a preview player that cannot play — and never
// let it reach the server/Whisper, where it fails as a confusing
// "transcription error".
const MIN_BLOB_BYTES = 1024

const GUM_ERROR_MESSAGES = {
  NotAllowedError:
    'Microphone access was denied. Allow microphone access for this site in your browser settings and try again.',
  NotFoundError: 'No microphone was found. Connect one and try again.',
  NotReadableError:
    'Your microphone could not be started — it may be in use by another app.',
}

export function useAudioRecorder() {
  const [isRecording, setIsRecording] = useState(false)
  const [audioBlob, setAudioBlob] = useState(null)
  const [audioUrl, setAudioUrl] = useState(null)
  const [error, setError] = useState(null)

  const recorderRef = useRef(null)
  const chunksRef = useRef([])
  const streamRef = useRef(null)
  // Set by recorder.onerror; onstop checks it and reports failure instead of
  // handing out a blob assembled from a broken capture.
  const failureRef = useRef(null)
  // Mirrors audioUrl so the unmount cleanup sees the CURRENT url — the
  // cleanup closure below has [] deps and previously captured the
  // first-render value (always null), leaking the blob URL.
  const audioUrlRef = useRef(null)

  const startRecording = useCallback(async () => {
    setError(null)
    setAudioBlob(null)
    setAudioUrl(null)
    chunksRef.current = []
    failureRef.current = null

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      const mimeType = pickMimeType()
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream)

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data)
        }
      }

      // Fires when capture dies mid-recording (Bluetooth headset switching,
      // device unplugged, OS-level input failure). Without this the timer
      // keeps ticking, nothing hits the console, and onstop silently
      // produces an empty blob.
      recorder.onerror = (event) => {
        failureRef.current =
          event?.error?.message || 'Recording failed partway through.'
        if (recorder.state !== 'inactive') {
          try {
            recorder.stop()
          } catch {
            /* already stopping */
          }
        }
        setIsRecording(false)
      }

      // The mic track ending (e.g. AirPods disconnect) does NOT error the
      // recorder — stop it so whatever data exists gets flushed and the
      // size guard in onstop can judge it.
      stream.getTracks().forEach((track) => {
        track.onended = () => {
          if (recorder.state === 'recording') {
            try {
              recorder.stop()
            } catch {
              /* already stopping */
            }
          }
          setIsRecording(false)
        }
      })

      recorder.onstop = () => {
        // recorder.mimeType is the authoritative type of what was actually
        // written; fall back to the first chunk's type (Safari has returned
        // '' here), then to the negotiated candidate.
        const type =
          recorder.mimeType || chunksRef.current[0]?.type || mimeType || 'audio/webm'
        const blob = new Blob(chunksRef.current, { type })

        streamRef.current?.getTracks().forEach((track) => track.stop())
        streamRef.current = null
        if (recorderRef.current === recorder) recorderRef.current = null

        if (failureRef.current || blob.size < MIN_BLOB_BYTES) {
          setError(
            failureRef.current ||
              'No audio was captured. Check that your microphone (or Bluetooth headset) is working, then try again.',
          )
          setAudioBlob(null)
          setAudioUrl(null)
          return
        }

        const url = URL.createObjectURL(blob)
        setAudioBlob(blob)
        setAudioUrl(url)
        audioUrlRef.current = url
      }

      // 1s timeslice: data accumulates as we go, so a mid-recording failure
      // still flushes everything captured up to that point (a single
      // end-of-recording flush can yield nothing on some platforms).
      recorder.start(1000)
      recorderRef.current = recorder
      setIsRecording(true)
    } catch (err) {
      console.error('useAudioRecorder: failed to start', err)
      setError(GUM_ERROR_MESSAGES[err.name] || err.message || 'Could not access microphone')
      streamRef.current?.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
  }, [])

  const stopRecording = useCallback(() => {
    if (recorderRef.current && isRecording) {
      recorderRef.current.stop()
      recorderRef.current = null
      setIsRecording(false)
    }
  }, [isRecording])

  const reset = useCallback(() => {
    setAudioBlob(null)
    setAudioUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return null
    })
    audioUrlRef.current = null
    chunksRef.current = []
    failureRef.current = null
    setError(null)
  }, [])

  // Cleanup blob URL on unmount (via ref — see audioUrlRef note above).
  useEffect(() => {
    return () => {
      if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current)
      streamRef.current?.getTracks().forEach((track) => track.stop())
    }
  }, [])

  return {
    isRecording,
    startRecording,
    stopRecording,
    reset,
    audioBlob,
    audioUrl,
    error,
  }
}
